import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { AreaTagI } from 'components/ImageTagger';
import { FTOStatusName } from 'constants/constants';
import _uniqueId from 'lodash/uniqueId';
import { ICPCClassification } from 'types/types';
import { FTOFeatureTreeType, FTOI, UpdatedScoresType } from '../types';
import { FeatureIdentifiersMap } from '../utils/featureIdentifiers';

interface SearchState {
  id: string;
  searchName: string;
  productDescription: string;
  pdf: { pdfId: string; pdfName: string }[];
  word: { wordId: string; wordName: string }[];
  images: { imageId: string; imageUrl: string }[];
  imagesFromSearchHistory: { imageId: string; imageUrl: string }[];
  imageTags: { imageId: string; tags: { tagName: string; tagId: string }[] }[];
  imageAreaTags: { imageId: string; areas: AreaTagI[] }[];
  imageAreaTagsTemp: { imageId: string; areas: AreaTagI[] }[];
  featureTree: FTOFeatureTreeType | null;
  featureIdentifiers: FeatureIdentifiersMap;
  highlightedFeatureId: string | null;
  activeHighlightPatentId: string | null;
  groupByFamily: boolean;
  countryCodes: string[];
  statuses: string[];
  assignees: string[];
  priorityFromDate: string;
  priorityToDate: string;
  patentsWithFeedback: string[];
  updatedScores: UpdatedScoresType | null;
  isDuplicateExist: boolean;
  updatedAt: string;
  regenerateTree: boolean;
  featureTreeLoading: boolean;
  selectPatentsMode: boolean;
  generateClaimsMode: boolean;
  patentsToGenerateClaims: string[];
  newPatentsToGenerateClaims: string[];
  selectedClaim: {
    claimId: string;
    patentId: string;
    sectionId: string;
    sectionPosition: number;
  } | null;
  isLoadingScore: boolean;
  selectedCPCClassifications: ICPCClassification[];
  isReanalyzing: boolean;
  reanalysisStatus: FTOStatusName | null;
  reanalysisProgress: number;
  selectedAnalysisId: string;
}

const initialState: SearchState = {
  id: '',
  searchName: '',
  productDescription: '',
  pdf: [],
  word: [],
  images: [],
  imagesFromSearchHistory: [],
  imageTags: [],
  imageAreaTags: [],
  imageAreaTagsTemp: [],
  featureTree: null,
  featureIdentifiers: {},
  highlightedFeatureId: null,
  activeHighlightPatentId: null,
  groupByFamily: false,
  countryCodes: [],
  statuses: [],
  assignees: [],
  priorityFromDate: '',
  priorityToDate: '',
  patentsWithFeedback: [],
  updatedScores: null,
  isDuplicateExist: false,
  updatedAt: '',
  regenerateTree: false,
  featureTreeLoading: false,
  generateClaimsMode: false,
  selectPatentsMode: false,
  patentsToGenerateClaims: [],
  newPatentsToGenerateClaims: [],
  selectedClaim: null,
  isLoadingScore: false,
  selectedCPCClassifications: [],
  isReanalyzing: false,
  reanalysisStatus: null,
  reanalysisProgress: 0,
  selectedAnalysisId: '',
};

const FTOSlice = createSlice({
  name: 'FTO',
  initialState,
  reducers: {
    setFTO_SearchName: (state, action: PayloadAction<string>) => {
      state.searchName = action.payload;
    },
    setFTO_Id: (state, action: PayloadAction<string>) => {
      state.id = action.payload;
    },
    setFTO_ProductDescription: (state, action: PayloadAction<string>) => {
      state.productDescription = action.payload;
    },
    setFTOGroupByFamily: (state, action: PayloadAction<boolean>) => {
      state.groupByFamily = action.payload;
    },
    setFTO_PDF: (state, action: PayloadAction<{ pdfId: string; pdfName: string }>) => {
      if (!state.pdf.find(file => file.pdfId === action.payload.pdfId)) {
        state.pdf = [...state.pdf, action.payload];
      }
    },
    removeFTO_PDF: (state, action: PayloadAction<string>) => {
      state.pdf = state.pdf.filter(file => file.pdfId !== action.payload);
    },
    setIsLoadingScore: (state, action: PayloadAction<boolean>) => {
      state.isLoadingScore = action.payload;
    },
    setFTO_Word: (state, action: PayloadAction<{ wordId: string; wordName: string }>) => {
      if (!state.word.find(file => file.wordId === action.payload.wordId)) {
        state.word = [...state.word, action.payload];
      }
    },
    removeFTO_Word: (state, action: PayloadAction<string>) => {
      state.word = state.word.filter(file => file.wordId !== action.payload);
    },
    setFTO_Images: (state, action: PayloadAction<{ imageId: string; imageUrl: string }>) => {
      if (!state.images.find(image => image.imageId === action.payload.imageId)) {
        state.images = [...state.images, action.payload];
      }
    },
    setFTO_ImagesFromSearchHistory: (state, action: PayloadAction<{ imageId: string; imageUrl: string }[]>) => {
      state.imagesFromSearchHistory = action.payload;
    },
    removeFTO_Image: (state, action: PayloadAction<string>) => {
      state.images = state.images.filter(img => img.imageId !== action.payload);
    },
    setFTO_ImageTags: (
      state,
      action: PayloadAction<{
        imageId: string;
        tags: { tagName: string; tagId: string }[];
      }>,
    ) => {
      const targetImage = state.imageTags.find(tag => tag.imageId === action.payload.imageId);

      if (targetImage) {
        targetImage.tags = action.payload.tags;
      } else {
        state.imageTags.push(action.payload);
      }
    },
    setFTO_FeatureTree: (state, action: PayloadAction<FTOFeatureTreeType | null>) => {
      state.featureTree = action.payload;
    },
    setFTO_FeatureIdentifiers: (state, action: PayloadAction<FeatureIdentifiersMap>) => {
      state.featureIdentifiers = action.payload;
    },
    setFTO_HighlightedFeatureId: (state, action: PayloadAction<string | null>) => {
      state.highlightedFeatureId = action.payload;
      if (action.payload === null) {
        state.activeHighlightPatentId = null;
      }
    },
    setFTO_ActiveHighlightPatentId: (state, action: PayloadAction<string | null>) => {
      state.activeHighlightPatentId = action.payload;
    },
    setFTO_FeatureTreeLoading: (state, action: PayloadAction<boolean>) => {
      state.featureTreeLoading = action.payload;
    },
    setFTO_ImageAreaTags: (
      state,
      action: PayloadAction<{
        imageId: string;
        area: AreaTagI;
      }>,
    ) => {
      const targetImage = state.imageAreaTags.find(tag => tag.imageId === action.payload.imageId);
      if (targetImage) {
        const area = targetImage.areas.find(area => area.id === action.payload.area.id);
        if (area) {
          area.tags = action.payload.area.tags;
        } else {
          targetImage.areas.push(action.payload.area);
        }
      } else {
        state.imageAreaTags.push({
          imageId: action.payload.imageId,
          areas: [action.payload.area],
        });
      }
    },
    setFTO_ImageAreaTagsTemp: (
      state,
      action: PayloadAction<{
        imageId: string;
        area: AreaTagI;
      }>,
    ) => {
      const targetImage = state.imageAreaTagsTemp.find(tag => tag.imageId === action.payload.imageId);
      if (targetImage) {
        const area = targetImage.areas.find(area => area.id === action.payload.area.id);
        if (area) {
          area.tags = action.payload.area.tags;
        } else {
          targetImage.areas.push(action.payload.area);
        }
      } else {
        state.imageAreaTagsTemp.push({
          imageId: action.payload.imageId,
          areas: [action.payload.area],
        });
      }
    },
    clearImageAreaTagsTemp: state => {
      state.imageAreaTagsTemp = [];
    },
    createArea: (
      state,
      action: PayloadAction<{
        imageId: string;
        area: AreaTagI;
      }>,
    ) => {
      const targetImage = state.imageAreaTagsTemp.find(tag => tag.imageId === action.payload.imageId);
      if (targetImage) {
        targetImage.areas.push(action.payload.area);
      } else {
        state.imageAreaTagsTemp.push({
          imageId: action.payload.imageId,
          areas: [action.payload.area],
        });
      }
    },
    changeAreaColor: (
      state,
      action: PayloadAction<{
        imageId: string;
        areaId: string;
        color: string;
        temp: boolean;
      }>,
    ) => {
      const targetImage = state.imageAreaTags.find(tag => tag.imageId === action.payload.imageId);
      const targetImageTemp = state.imageAreaTagsTemp.find(tag => tag.imageId === action.payload.imageId);

      if (action.payload.temp) {
        if (targetImageTemp) {
          const area = targetImageTemp.areas.find(area => area.id === action.payload.areaId);
          if (area) {
            area.color = action.payload.color;
          }
        }
      } else {
        if (targetImage) {
          const area = targetImage.areas.find(area => area.id === action.payload.areaId);
          if (area) {
            area.color = action.payload.color;
          }
        }
      }
    },
    deleteAreaFromImage: (
      state,
      action: PayloadAction<{
        imageId: string;
        areaId: string;
      }>,
    ) => {
      const targetImage = state.imageAreaTags.find(tag => tag.imageId === action.payload.imageId);
      if (targetImage) {
        targetImage.areas = targetImage.areas.filter(area => area.id !== action.payload.areaId);
      }
    },
    deleteTempAreaFromImage: (
      state,
      action: PayloadAction<{
        imageId: string;
        areaId: string;
      }>,
    ) => {
      const targetImage = state.imageAreaTagsTemp.find(tag => tag.imageId === action.payload.imageId);
      if (targetImage) {
        targetImage.areas = targetImage.areas.filter(area => area.id !== action.payload.areaId);
      }
    },
    setFTO_CountryCodes: (state, action: PayloadAction<string[]>) => {
      state.countryCodes = action.payload;
    },
    setFTO_Statuses: (state, action: PayloadAction<string[]>) => {
      state.statuses = action.payload;
    },
    setFTO_PriorityFromDate: (state, action: PayloadAction<string>) => {
      state.priorityFromDate = action.payload;
    },
    setFTO_Assignees: (state, action: PayloadAction<string[]>) => {
      state.assignees = action.payload;
    },
    setFTO_PriorityToDate: (state, action: PayloadAction<string>) => {
      state.priorityToDate = action.payload;
    },
    setFTO_PatentWithFeedback: (state, action: PayloadAction<string>) => {
      state.patentsWithFeedback.push(action.payload);
    },
    setFTO_UpdatedScores: (state, action: PayloadAction<UpdatedScoresType>) => {
      state.updatedScores = action.payload;
    },
    setFTO_UpdatedAt: (state, action: PayloadAction<string>) => {
      state.updatedAt = action.payload;
    },
    setFTO_Duplicate: (state, action: PayloadAction<FTOI | null>) => {
      if (action.payload) {
        state.isDuplicateExist = true;
        state.id = action.payload.infringementDetectionId;
        state.searchName = action.payload.name;
        state.productDescription = action.payload.productDescription;
        state.images = action.payload.images.map(i => ({
          imageId: i.imageId,
          imageUrl: i.imageUrl,
        }));
        state.imageTags = action.payload.images.map(i => ({
          imageId: i.imageId,
          tags: i.imageTags.map(t => {
            const id = _uniqueId('tag-to-add-');

            return { tagName: t.tag, tagId: id };
          }),
        }));
        state.imageAreaTags = action.payload.images.map(i => ({
          imageId: i.imageId,
          areas: i.imageAreaTags.map(area => {
            return {
              id: area.imageTagAreaId,
              tags: area.tags.map(tag => ({ id: tag.tagId, name: tag.tag })),
              x: area.tagArea.xCoordinate,
              y: area.tagArea.yCoordinate,
              width: area.tagArea.width,
              height: area.tagArea.height,
              color: area.colour,
            };
          }),
        }));
        state.groupByFamily = action.payload.filters.groupByFamily;
        state.countryCodes = action.payload.filters.termFilters.find(f => f.field === 'COUNTRY_CODE')?.values ?? [];
        state.assignees = action.payload.filters.termFilters.find(f => f.field === 'APPLICANT')?.values ?? [];
        state.statuses = action.payload.filters.termFilters.find(f => f.field === 'PATENT_STATUS')?.values ?? [];
        state.priorityFromDate =
          action.payload.filters.dateRangeFilters.find(f => f.filterField === 'PRIORITY_DATE')?.fromDate ?? '';
        state.priorityToDate =
          action.payload.filters.dateRangeFilters.find(f => f.filterField === 'PRIORITY_DATE')?.toDate ?? '';
      } else {
        state.isDuplicateExist = false;
      }
    },
    regenerateFeatureTree: (state, action: PayloadAction<boolean>) => {
      state.regenerateTree = action.payload;
    },
    setSelectPatentsMode: (state, action: PayloadAction<boolean>) => {
      state.selectPatentsMode = action.payload;
    },
    setPatentsToGenerateClaims: (state, action: PayloadAction<string[]>) => {
      state.patentsToGenerateClaims = action.payload;
    },
    setNewPatentsToGenerateClaims: (state, action: PayloadAction<string[]>) => {
      state.newPatentsToGenerateClaims = action.payload;
    },
    setGenerateClaimsMode: (state, action: PayloadAction<boolean>) => {
      state.generateClaimsMode = action.payload;
    },
    setSelectedClaim: (
      state,
      action: PayloadAction<{
        claimId: string;
        patentId: string;
        sectionId: string;
        sectionPosition: number;
      } | null>,
    ) => {
      state.selectedClaim = action.payload;
    },
    setFTO_CPCClassifications: (state, action: PayloadAction<ICPCClassification[]>) => {
      state.selectedCPCClassifications = action.payload;
    },
    setFTO_Reanalyzing: (state, action: PayloadAction<boolean>) => {
      state.isReanalyzing = action.payload;
      if (!action.payload) {
        state.reanalysisStatus = null;
        state.reanalysisProgress = 0;
      }
    },
    setFTO_ReanalysisStatus: (state, action: PayloadAction<FTOStatusName | null>) => {
      state.reanalysisStatus = action.payload;

      // Update progress based on status
      switch (action.payload) {
        case FTOStatusName.FTO_ANALYSIS_QUEUED:
          state.reanalysisProgress = 50;
          break;
        case FTOStatusName.EXTRACTING_FEATURES:
          state.reanalysisProgress = 60;
          break;
        case FTOStatusName.RUNNING_COMPARISON:
          state.reanalysisProgress = 80;
          break;
        case FTOStatusName.PREPARING_RESULTS:
          state.reanalysisProgress = 90;
          break;
        case FTOStatusName.COMPLETE:
          state.reanalysisProgress = 100;
          break;
        case null:
          state.reanalysisProgress = 0;
          break;
      }
    },
    setFTO_ReanalysisProgress: (state, action: PayloadAction<number>) => {
      state.reanalysisProgress = action.payload;
    },
    setFTO_SelectedAnalysisId: (state, action: PayloadAction<string>) => {
      state.selectedAnalysisId = action.payload;
    },
    resetFTOSlice: () => initialState,
  },
});

export default FTOSlice.reducer;

// Action creators are generated for each case reducer function
export const {
  setFTO_SearchName,
  setFTO_Id,
  setFTO_ProductDescription,
  setFTO_Images,
  removeFTO_Image,
  setFTO_ImageTags,
  setFTO_ImageAreaTags,
  setFTO_ImageAreaTagsTemp,
  changeAreaColor,
  setFTO_ImagesFromSearchHistory,
  setFTO_FeatureTree,
  setFTO_FeatureIdentifiers,
  setFTO_FeatureTreeLoading,
  setFTOGroupByFamily,
  setFTO_CountryCodes,
  setFTO_Statuses,
  setFTO_Assignees,
  setFTO_CPCClassifications,
  setFTO_PriorityFromDate,
  setFTO_PriorityToDate,
  setFTO_PatentWithFeedback,
  setFTO_UpdatedScores,
  setFTO_UpdatedAt,
  setFTO_Duplicate,

  resetFTOSlice,
  createArea,
  deleteAreaFromImage,
  deleteTempAreaFromImage,
  clearImageAreaTagsTemp,

  setFTO_PDF,
  removeFTO_PDF,
  setFTO_Word,
  removeFTO_Word,
  setIsLoadingScore,
  setFTO_Reanalyzing,
  setFTO_ReanalysisStatus,

  setFTO_SelectedAnalysisId,
  setFTO_HighlightedFeatureId,
  setFTO_ActiveHighlightPatentId,
} = FTOSlice.actions;
